<template>
  <div class="vc-support-ticket-list">
    <div v-if="!isContainerInited" class="d-flex align-items-center justify-content-center" style="min-height:150px">
      <div class="spinner-border mx-2" role="status"></div>
    </div>

    <div v-else-if="!hasSession" class="alert alert-warning d-flex align-items-center" role="alert">
      <i class="bi bi-lock me-3 fs-4"></i>
      <div class="flex-grow-1">
        <strong>{{ $t('messages.signInPrompt') }}</strong>
      </div>
      <a class="btn btn-primary btn-sm" href="#" @click.prevent="window.dialogManager?.showSignIn()">
        {{ $t('messages.signIn') }}
      </a>
    </div>

    <div v-else-if="isLoading" class="d-flex align-items-center justify-content-center" style="min-height:150px">
      <div class="spinner-border mx-2" role="status"></div>
    </div>

    <div v-else-if="loadError" class="alert alert-danger" role="alert">
      {{ loadError }}
      <div class="mt-2"><button class="btn btn-sm btn-outline-danger" @click="loadTickets">{{ $t('messages.refresh') }}</button></div>
    </div>

    <div v-else>
      <div class="mb-3">
        <div class="btn-group btn-group-sm" role="group">
          <button type="button" class="btn" :class="showAll ? 'btn-outline-secondary' : 'btn-secondary'"
            @click="setShowAll(false)">{{ $t('messages.filterOpen') }}</button>
          <button type="button" class="btn" :class="showAll ? 'btn-secondary' : 'btn-outline-secondary'"
            @click="setShowAll(true)">{{ $t('messages.filterAll') }}</button>
        </div>
      </div>

      <div v-if="!tickets.length" class="alert alert-light border text-center py-4">
        {{ showAll ? $t('messages.noTickets') : $t('messages.noOpenTickets') }}
        <div class="mt-2">
          <a href="#" @click.prevent="$emit('new-ticket')">{{ $t('messages.openNewTicket') }}</a>
        </div>
      </div>

      <div v-else class="list-group">
        <a v-for="ticket in tickets" :key="ticket.id" href="#" class="list-group-item list-group-item-action py-3"
          :class="{ 'border-start border-warning border-3': ticket.status === 'pending' }"
          @click.prevent="$emit('open-ticket', ticket.id)">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <div class="fw-semibold">{{ ticket.subject }}</div>
              <div class="text-muted small">{{ $t('messages.ticketNumber', [ticket.id]) }}</div>
            </div>
            <span class="badge rounded-pill" :class="statusBadgeClass(ticket.status)">{{ statusLabel(ticket.status) }}</span>
          </div>
        </a>
      </div>

      <div v-if="hasMore" class="text-center mt-3">
        <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoadingMore" @click="loadMore">
          <span v-if="isLoadingMore" class="spinner-border spinner-border-sm me-1"></span>
          {{ $t('messages.loadMore') }}
        </button>
      </div>

      <div v-if="!showAll && recentlyClosed.length" class="mt-4">
        <h6 class="text-muted mb-2">{{ $t('messages.recentlyClosed') }}</h6>
        <div class="list-group">
          <a v-for="ticket in recentlyClosed" :key="ticket.id" href="#" class="list-group-item list-group-item-action py-2"
            @click.prevent="$emit('open-ticket', ticket.id)">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="fw-semibold small">{{ ticket.subject }}</div>
                <div class="text-muted small">{{ $t('messages.ticketNumber', [ticket.id]) }}</div>
              </div>
              <span class="badge rounded-pill" :class="statusBadgeClass(ticket.status)">{{ statusLabel(ticket.status) }}</span>
            </div>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "/src/visualcrossing-core/vccommunicator_support";
import translations from '@/translations/supportticket.js';

/** Ticket list view (design doc §8.1) - Mode A only, session-gated. */
export default {
  name: 'TicketList',
  i18n: {
    messages: {
      en: translations.en,
      de: translations.de,
    },
  },
  data() {
    return {
      isContainerInited: false,
      sessionId: null,
      isLoading: false,
      isLoadingMore: false,
      loadError: null,
      tickets: [],
      hasMore: false,
      nextPage: 1,
      showAll: false,
      recentlyClosed: [],
    };
  },
  computed: {
    hasSession() {
      return !!this.sessionId;
    },
  },
  methods: {
    statusLabel(status) {
      var map = {
        new: 'statusNew', open: 'statusOpen', pending: 'statusPending',
        hold: 'statusHold', solved: 'statusSolved', closed: 'statusClosed',
      };
      var key = map[status];
      return key ? this.$t('messages.' + key) : (status || '');
    },
    statusBadgeClass(status) {
      if (status === 'solved' || status === 'closed') return 'bg-secondary';
      if (status === 'pending') return 'bg-warning text-dark';
      return 'bg-primary';
    },
    setShowAll(value) {
      if (this.showAll === value) return;
      this.showAll = value;
      this.loadTickets();
    },
    // The list endpoint requires a real session and returns a genuine HTTP 401/error page (not the
    // usual 200+errorCode envelope) when the session is missing/expired - vcCommunicator surfaces
    // that as the raw response body, so translate it into something a user can actually act on.
    friendlyError(error) {
      var text = '' + error;
      if (/\b401\b|Unauthorized/i.test(text)) {
        this.sessionId = null;
        return this.$t('messages.signInPrompt');
      }
      if (/^\s*<!doctype|<html/i.test(text)) {
        return this.$t('messages.loadFailed');
      }
      return text;
    },
    // status is filtered server-side (Zendesk Search Requests with status=new,open,pending,hold) -
    // filtering an already-sorted, already-paginated page client-side would be wrong, since a
    // recently-touched closed ticket can rank above an older open one and push it off the page.
    loadTickets() {
      var me = this;
      if (!me.hasSession) return;
      me.isLoading = true;
      me.loadError = null;
      me.tickets = [];
      window.vcCommunicator.listMySupportTickets(me.showAll ? 'all' : 'open', 1)
        .then(function (result) {
          me.isLoading = false;
          me.tickets = result.tickets || [];
          me.hasMore = !!result.hasMore;
          me.nextPage = result.nextPage || 2;
          me.recentlyClosed = result.recentlyClosed || [];
        })
        .catch(function (error) {
          me.isLoading = false;
          me.loadError = me.friendlyError(error);
        });
    },
    loadMore() {
      var me = this;
      if (!me.hasMore || me.isLoadingMore) return;
      me.isLoadingMore = true;
      window.vcCommunicator.listMySupportTickets(me.showAll ? 'all' : 'open', me.nextPage)
        .then(function (result) {
          me.isLoadingMore = false;
          me.tickets = me.tickets.concat(result.tickets || []);
          me.hasMore = !!result.hasMore;
          me.nextPage = result.nextPage || (me.nextPage + 1);
        })
        .catch(function (error) {
          me.isLoadingMore = false;
          me.loadError = me.friendlyError(error);
        });
    },
  },
  mounted() {
    var me = this;
    var vcCommunicator = window.vcCommunicator;
    if (!vcCommunicator) {
      console.log('No vcCommunicator');
      return;
    }
    me.sessionId = vcCommunicator.sessionId;
    me.isContainerInited = vcCommunicator.isInited || me.sessionId;

    vcCommunicator.addHandler('createsession', function (sessionId) {
      me.sessionId = sessionId;
      me.isContainerInited = true;
      me.loadTickets();
    });
    vcCommunicator.addHandler('createsessionfailed', function () {
      me.sessionId = null;
      me.isContainerInited = true;
    });
    vcCommunicator.addHandler('terminatesession', function () {
      me.sessionId = null;
      me.tickets = [];
    });

    if (me.hasSession) {
      me.loadTickets();
    }
  },
};
</script>
